<template>

    <validation-observer ref="panelistsObserver" v-slot="{ invalid }">

        <v-container fluid class="pa-0">
            <v-row dense>

                <!-- PASTE LIST -->
                <v-col cols="6">

                    <v-alert v-if="pasteBoxError" :type="pasteBoxError.type" class="body-2" icon="mdi-alert" prominent>
                        {{ pasteBoxError.message }}
                    </v-alert>

                    <validation-provider name="Identifiers"
                                         rules="required"
                                         v-slot="{errors}">

                        <v-textarea hide-details
                                    v-model="ids"
                                    solo
                                    rows="10"
                                    spellcheck="false"
                                    @input="pasted"
                                    class="monospace body-2"/>

                    </validation-provider>

                </v-col>

                <!-- ERROR LINES -->
                <v-col cols="6">
                    <div v-show="failedIds.length>0">

                        <v-alert v-if="failedIds.length > 0" type="error" class="body-2" icon="mdi-arrow-down" prominent>
                            Removed identifiers
                        </v-alert>

                        <v-textarea v-model="failedText"
                                    solo
                                    rows="10"
                                    spellcheck="false"
                                    :disabled="true"
                                    class="monospace red--text body-2"
                        ></v-textarea>
                    </div>
                </v-col>

            </v-row>

            <v-row>
                <v-col cols="12" class="pt-0">

                    <v-tooltip top color="primary" v-if="showConfirmButton">
                        <template v-slot:activator="{ on }">
                            <v-btn v-show="cleanedIds.length > 0" v-on="on" color="primary" @click="confirmClicked">Confirm</v-btn>
                        </template>
                        <span>
                            Confirm identifiers
                        </span>
                    </v-tooltip>

                    <v-tooltip top color="primary">
                        <template v-slot:activator="{ on }">
                            <v-btn v-on="on" class="mt-2" text @click="resetClicked">Reset</v-btn>
                        </template>
                        <span>
                            Reset pasted identifiers list and any errors reported
                        </span>
                    </v-tooltip>
                </v-col>
            </v-row>
        </v-container>
    </validation-observer>
</template>

<script>

/*
cffbeb47-bc75-4ad7-a6af-0a2ab7efab55
0299346d-27b2-4eae-b9dd-29c3356b8cea
8c54d3a7-4a4f-4689-ad37-a7ff079e25bb
494d4c81-f109-4551-8929-0870d9b79d84
42c55e72-e6ba-43f1-b2cf-5bc188dce6d2
c8e18294-ce23-4ba6-8dda-4e3b0287fe51
c8e18294-ce23-4ba6-8dda-4e3b0287fe51
c8e18294-ce23-4ba6-8dda-4e3b0287feXX
f6ffad95-ac5e-49fe-be0a-da5ef7d79383
03f89efa-8882-4745-be49-0473bf95f061
0d668a39-8343-4186-9f0a-095bb3ff7734
09cd09ae-7bd1-4fc3-bc9f-4f741cd10e1a
*/

import {validate} from "uuid";

export default {

    name: "IdentifiersPasteBox",

    props: {
        showConfirmButton: {
            type: Boolean,
            required: false,
            default: false,
        },

        confirmButtonText: {
            type: String,
            required: false,
            default: 'Confirm'
        }
    },

    data() {
        return {
            ids: [],
            cleanedIds: [],
            failedIds: [],
            pasteErrors: []
        }
    },

    computed: {
        failedText() {
            return this.failedIds
                .filter(id => id !== '')
                .join("\n");
        },

        pasteBoxError() {
            if (this.ids.length === 0 && this.failedIds.length > 0) {
                return {
                    message: "Error: All identifiers were invalid!",
                    type: "error"
                }
            }

            if (this.failedIds.length > 0) {
                return {
                    message: "Warning! Removed " + this.pasteErrors.join(" and "),
                    type: "warning"
                }
            }
        }
    },
    methods: {

        pasted() {

            this.pasteErrors = [];

            this.failedIds = [];

            let identifiers = this.ids
                .replaceAll('"', '')
                .replaceAll(' ', '')
                .replaceAll(",", "\n")
                .replaceAll(/(\r\n|\n|\r)/gm, "\n")
                .split("\n");

            this.cleanedIds = identifiers.filter(id => {

                const result = validate(id);

                if (!result) {
                    if (id !== '') this.failedIds.push(id);
                }

                return result;
            });

            if (this.failedIds.length > 0) {
                this.pasteErrors.push(this.failedIds.length + " invalid ID(s)");
            }

            const countBefore = this.cleanedIds.length;

            let nonDuplicates = [];

            this.cleanedIds.forEach(id => {
                if (nonDuplicates.includes(id)) {
                    this.failedIds.push(id);
                } else {
                    nonDuplicates.push(id);
                }
            });

            // Remove duplicates
            this.cleanedIds = nonDuplicates;

            if (countBefore > this.cleanedIds.length) {
                const removedCount = countBefore - this.cleanedIds.length;
                this.pasteErrors.push(removedCount + ' duplicate IDs!');
            }

            if (this.cleanedIds.length > 1000) {
                Bus.$emit('flash-message', {type: 'error', message: 'Maximum number of identifiers is 1000'});
                return;
            }

            this.ids = this.cleanedIds.join("\n");

            this.emit();
        },

        emit() {

            if (this.$refs.panelistsObserver) {
                this.$nextTick(() => {
                    this.$refs.panelistsObserver.validate()
                        .then(isValid => {
                            if (isValid) {
                                this.$emit('change', this.cleanedIds);
                            }
                        });
                })
            }
        },

        confirmClicked() {
            this.emit();
            this.$emit('confirm', this.cleanedIds);
        },

        resetClicked() {
            this.ids = '';
            this.cleanedIds = [];
            this.failedIds = [];
        },
    }
}
</script>

<style scoped>

.monospace {
    font-family: "Lucida Console", Courier, monospace !important;
    font-size: 1.1em !important;
}

</style>